import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loader from '../components/Loader';
import { useT } from '../i18n';
import './AdminUsersPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

// Plan configuration
const PLAN_CONFIG = {
  basic: { label: 'Basic', limit: 1, color: 'basic' },
  advanced: { label: 'Advanced', limit: 3, color: 'advanced' },
  corporate: { label: 'Corporate', limit: 100, color: 'corporate' }
};

const AdminUsersPage = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Held only until the admin dismisses it; never persisted anywhere.
  const [tempPassword, setTempPassword] = useState(null);
  
  // Downgrade modal state
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgradeUser, setDowngradeUser] = useState(null);
  const [downgradeNewPlan, setDowngradeNewPlan] = useState(null);
  const [userBuildings, setUserBuildings] = useState([]);
  const [selectedBuildingIds, setSelectedBuildingIds] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);

  // Side panel state
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [sidePanelUser, setSidePanelUser] = useState(null);
  const [sidePanelBuildings, setSidePanelBuildings] = useState([]);
  const [sidePanelLoading, setSidePanelLoading] = useState(false);
  const [restoringBuildingId, setRestoringBuildingId] = useState(null);
  const [editingBuildingId, setEditingBuildingId] = useState(null);
  const [editingBuildingName, setEditingBuildingName] = useState('');
  const [savingBuildingId, setSavingBuildingId] = useState(null);
  const [archivingBuildingId, setArchivingBuildingId] = useState(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null, user: null });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Calculate plan statistics
  const planStats = useMemo(() => {
    const stats = { basic: 0, advanced: 0, corporate: 0, total: users.length };
    users.forEach(user => {
      const plan = user.subscription_plan || 'basic';
      if (stats[plan] !== undefined) stats[plan]++;
    });
    return stats;
  }, [users]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.toLowerCase().trim();
    return users.filter(user => 
      user.username?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.full_name?.toLowerCase().includes(term) ||
      user.phone?.includes(term)
    );
  }, [users, searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // The admin-password prompt is gone — see AdminTokensPage for the reasoning.
  // It also validated by calling the registration-tokens endpoint with Basic
  // auth, which the server no longer accepts, so it had become a gate that could
  // never open. Access is users.role, enforced server-side on every request.
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async (showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    }
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      if (err.response?.status === 403) {
        setError(t('admu.accessDenied'));
      } else {
        setError(err.response?.data?.message || t('admu.loadUsersFailed'));
      }
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/users/${userId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(t('admu.statusChanged', { status: newStatus }));
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh users list silently
      fetchUsers(false);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.response?.data?.message || t('admu.statusChangeFailed'));
      setTimeout(() => setError(''), 3000);
    }
  };

  /*
   * Set a temporary password and show it to the admin once.
   *
   * Email reset is the normal path, but support cannot depend on it: it needs
   * the customer to receive mail from us, which is the very thing that fails
   * for a young domain or a strict corporate filter. This gives a locked-out
   * customer a way back in that does not involve their inbox at all.
   */
  const resetUserPassword = async (user) => {
    setTempPassword(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/users/${user.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTempPassword({
        username: res.data.username,
        email: res.data.email,
        password: res.data.tempPassword
      });
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.message || t('admu.resetPasswordFailed'));
      setTimeout(() => setError(''), 4000);
    }
  };

  const updateUserPlan = async (userId, newPlan, keepBuildingIds = null) => {
    try {
      const token = localStorage.getItem('token');
      const payload = { plan: newPlan };
      if (keepBuildingIds) {
        payload.keepBuildingIds = keepBuildingIds;
      }
      
      const response = await axios.patch(
        `${API_URL}/api/admin/users/${userId}/plan`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Close modal if open
      setShowDowngradeModal(false);
      setDowngradeUser(null);
      setDowngradeNewPlan(null);
      setUserBuildings([]);
      setSelectedBuildingIds([]);

      const archivedCount = response.data.archivedCount || 0;
      if (archivedCount > 0) {
        setSuccessMessage(t('admu.planChangedArchived', { plan: PLAN_CONFIG[newPlan]?.label, count: archivedCount }));
      } else {
        setSuccessMessage(t('admu.planChanged', { plan: PLAN_CONFIG[newPlan]?.label || newPlan }));
      }
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh users list silently
      fetchUsers(false);
    } catch (err) {
      console.error('Error updating user plan:', err);
      
      // Check if we need to show building selection modal
      if (err.response?.data?.requiresSelection) {
        const user = users.find(u => u.id === userId);
        setDowngradeUser(user);
        setDowngradeNewPlan(newPlan);
        fetchUserBuildings(userId);
        setShowDowngradeModal(true);
      } else {
        setError(err.response?.data?.message || t('admu.planChangeFailed'));
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const fetchUserBuildings = async (userId) => {
    setLoadingBuildings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/users/${userId}/buildings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Only show active (non-archived) buildings for selection
      const activeBuildings = response.data.buildings.filter(b => !b.archived);
      setUserBuildings(activeBuildings);
      setSelectedBuildingIds([]);
    } catch (err) {
      console.error('Error fetching user buildings:', err);
      setError(t('admu.loadBuildingsFailed'));
    } finally {
      setLoadingBuildings(false);
    }
  };

  const handleBuildingSelect = (buildingId) => {
    const newLimit = PLAN_CONFIG[downgradeNewPlan]?.limit || 1;
    
    if (selectedBuildingIds.includes(buildingId)) {
      // Deselect
      setSelectedBuildingIds(prev => prev.filter(id => id !== buildingId));
    } else {
      // Select (only if under limit)
      if (selectedBuildingIds.length < newLimit) {
        setSelectedBuildingIds(prev => [...prev, buildingId]);
      }
    }
  };

  const confirmDowngrade = () => {
    if (!downgradeUser || !downgradeNewPlan) return;
    
    const newLimit = PLAN_CONFIG[downgradeNewPlan]?.limit || 1;
    if (selectedBuildingIds.length !== newLimit) {
      setError(t('admu.selectExactly', { count: newLimit }));
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    updateUserPlan(downgradeUser.id, downgradeNewPlan, selectedBuildingIds);
  };

  const cancelDowngrade = () => {
    setShowDowngradeModal(false);
    setDowngradeUser(null);
    setDowngradeNewPlan(null);
    setUserBuildings([]);
    setSelectedBuildingIds([]);
    // Refresh to reset select dropdowns silently
    fetchUsers(false);
  };

  // Side panel functions
  const openBuildingsPanel = async (user) => {
    setSidePanelUser(user);
    setShowSidePanel(true);
    setSidePanelLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/users/${user.id}/buildings-detail`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSidePanelBuildings(response.data.buildings || []);
      // Update user info from response
      setSidePanelUser(prev => ({
        ...prev,
        planLimit: response.data.planLimit,
        activeCount: response.data.activeCount,
        archivedCount: response.data.archivedCount
      }));
    } catch (err) {
      console.error('Error fetching building details:', err);
      setError(t('admu.loadBuildingDetailsFailed'));
      setTimeout(() => setError(''), 3000);
    } finally {
      setSidePanelLoading(false);
    }
  };

  const closeSidePanel = () => {
    setShowSidePanel(false);
    setSidePanelUser(null);
    setSidePanelBuildings([]);
  };

  const restoreBuilding = async (buildingId) => {
    setRestoringBuildingId(buildingId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/buildings/${buildingId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state immediately (no re-fetch)
      setSidePanelBuildings(prev => prev.map(b => 
        b.id === buildingId ? { ...b, archived: false, archived_at: null } : b
      ));
      
      // Refresh the main user list silently in background (no loader)
      fetchUsers(false);
      
      setSuccessMessage(t('admu.buildingRestored'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error restoring building:', err);
      setError(err.response?.data?.message || t('admu.restoreBuildingFailed'));
      setTimeout(() => setError(''), 3000);
    } finally {
      setRestoringBuildingId(null);
    }
  };

  const startEditingBuilding = (building) => {
    setEditingBuildingId(building.id);
    setEditingBuildingName(building.name || '');
  };

  const cancelEditingBuilding = () => {
    setEditingBuildingId(null);
    setEditingBuildingName('');
  };

  const saveBuildingName = async (buildingId) => {
    if (!editingBuildingName.trim()) {
      setError(t('admu.buildingNameEmpty'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSavingBuildingId(buildingId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/buildings/${buildingId}`,
        { name: editingBuildingName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSidePanelBuildings(prev => prev.map(b => 
        b.id === buildingId ? { ...b, name: editingBuildingName.trim() } : b
      ));
      
      setEditingBuildingId(null);
      setEditingBuildingName('');
      
      // Refresh the main user list silently
      fetchUsers(false);
      
      setSuccessMessage(t('admu.nameSaved'));
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error saving building name:', err);
      setError(err.response?.data?.message || t('admu.saveNameFailed'));
      setTimeout(() => setError(''), 3000);
    } finally {
      setSavingBuildingId(null);
    }
  };

  const archiveBuilding = async (buildingId) => {
    setArchivingBuildingId(buildingId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/buildings/${buildingId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state immediately (no re-fetch)
      setSidePanelBuildings(prev => prev.map(b => 
        b.id === buildingId ? { ...b, archived: true, archived_at: new Date().toISOString() } : b
      ));
      
      // Refresh the main user list silently in background (no loader)
      fetchUsers(false);
      
      setSuccessMessage(t('admu.buildingArchived'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error archiving building:', err);
      setError(err.response?.data?.message || t('admu.archiveBuildingFailed'));
      setTimeout(() => setError(''), 3000);
    } finally {
      setArchivingBuildingId(null);
    }
  };

  const getBuildingDisplayName = (building) => {
    if (building.name && building.name !== 'Unnamed') {
      return building.name;
    }
    return t('admu.buildingNumber', { id: building.id });
  };

  // Show confirmation modal
  const showConfirmModal = (type, user) => {
    setConfirmModal({ show: true, type, user });
  };

  const hideConfirmModal = () => {
    setConfirmModal({ show: false, type: null, user: null });
  };

  const handleConfirmAction = async () => {
    const { type, user } = confirmModal;
    hideConfirmModal();

    if (type === 'delete') {
      await executeDeleteUser(user.id, user.username);
    } else if (type === 'disable') {
      await updateUserStatus(user.id, 'disabled');
    } else if (type === 'enable') {
      await updateUserStatus(user.id, 'active');
    }
  };

  const executeDeleteUser = async (userId, username) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(t('admu.userDeleted', { username }));
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh users list silently
      fetchUsers(false);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || t('admu.deleteUserFailed'));
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      // 'pending' is a self-serve signup nobody has actioned yet: a lead, not a
      // broken account. It reads as work waiting, not as a fault.
      pending: { label: 'Pending', className: 'info' },
      active: { label: 'Active', className: 'success' },
      disabled: { label: 'Disabled', className: 'error' },
      suspended: { label: 'Suspended', className: 'warning' }
    };
    
    const statusInfo = statusMap[status] || { label: status, className: 'default' };
    return <span className={`badge badge-${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  if (loading) {
    return <Loader fullscreen={false} />;
  }

  if (error && users.length === 0) {
    return (
      <div className="admin-users-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <div className="page-header-top">
          <button className="back-button" onClick={() => navigate('/buildings')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('admu.backToApp')}
          </button>
        </div>
        <h1>{t('admu.title')}</h1>
        <p className="subtitle">{t('admu.subtitle')}</p>
      </div>

      {/* Plan Statistics Bar */}
      <div className="stats-bar">
        <div className="stat-card stat-total">
          <div className="stat-value">{planStats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card stat-basic">
          <div className="stat-value">{planStats.basic}</div>
          <div className="stat-label">Basic</div>
        </div>
        <div className="stat-card stat-advanced">
          <div className="stat-value">{planStats.advanced}</div>
          <div className="stat-label">Advanced</div>
        </div>
        <div className="stat-card stat-corporate">
          <div className="stat-value">{planStats.corporate}</div>
          <div className="stat-label">Corporate</div>
        </div>
      </div>

      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Search Box */}
      <div className="search-box">
        <div className="search-input-wrapper">
          <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder={t('admu.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="search-clear" 
              onClick={() => setSearchTerm('')}
              title={t('admu.clearSearch')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-count">
            {filteredUsers.length === 1 ? t('admu.resultCount', { count: filteredUsers.length }) : t('admu.resultsCount', { count: filteredUsers.length })}
          </div>
        )}
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>{t('admu.user')}</th>
              <th>{t('admu.contact')}</th>
              <th>{t('admu.status')}</th>
              <th>{t('admu.plan')}</th>
              <th style={{textAlign: 'center'}}>{t('admu.buildings')}</th>
              <th>{t('admu.created')}</th>
              <th className="text-right">{t('admu.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm ? t('admu.noSearchResults') : t('admu.noUsers')}
                </td>
              </tr>
            ) : (
              filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage).map((user) => {
                const currentPlan = user.subscription_plan || 'basic';
                const buildingCount = user.building_count || 0;
                const buildingLimit = user.building_limit || PLAN_CONFIG[currentPlan]?.limit || 1;
                const isAtLimit = buildingCount >= buildingLimit;
                
                return (
                  <tr key={user.id} onClick={() => openBuildingsPanel(user)} style={{cursor: 'pointer'}}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">{user.username.charAt(0).toUpperCase()}</div>
                        <div>
                          <div className="user-name">{user.full_name || user.username}</div>
                          <div className="user-username">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="contact-info">
                        <div>{user.email}</div>
                        {user.phone && <div className="text-muted">{user.phone}</div>}
                      </div>
                    </td>
                    <td>{getStatusBadge(user.status)}</td>
                    <td>
                      <select
                        className={`plan-select plan-${currentPlan}`}
                        value={currentPlan}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateUserPlan(user.id, e.target.value)}
                      >
                        <option value="basic">Basic (1)</option>
                        <option value="advanced">Advanced (3)</option>
                        <option value="corporate">Corporate (100)</option>
                      </select>
                    </td>
                    {/* Buildings Column */}
                    <td style={{textAlign: 'center'}}>
                      <div 
                        className={`quota-badge ${buildingCount > buildingLimit ? 'over-limit' : isAtLimit ? 'at-limit' : 'under-limit'}`}
                      >
                        <span className="quota-current">{buildingCount}</span>
                        <span className="quota-separator">/</span>
                        <span className="quota-limit">{buildingLimit}</span>
                        {user.archived_count > 0 && (
                          <span className="quota-archived">{t('admu.archivedAbbrev', { count: user.archived_count })}</span>
                        )}
                      </div>
                    </td>
                    <td className="text-muted">{formatDate(user.created_at)}</td>
                    <td>
                      <div className="actions">
                        {user.status === 'active' ? (
                          <button
                            className="btn btn-icon-text btn-warning-soft"
                            onClick={(e) => { e.stopPropagation(); showConfirmModal('disable', user); }}
                            title={t('admu.disableUserTitle')}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <path d="M4.93 4.93L19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            {t('admu.disable')}
                          </button>
                        ) : (
                          <button
                            className="btn btn-icon-text btn-success-soft"
                            onClick={(e) => { e.stopPropagation(); showConfirmModal('enable', user); }}
                            title={t('admu.enableUserTitle')}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {t('admu.enable')}
                          </button>
                        )}
                        <button
                          className="btn btn-icon-text"
                          onClick={(e) => { e.stopPropagation(); resetUserPassword(user); }}
                          title={t('admu.setTempPasswordTitle')}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          {t('admu.passwordButton')}
                        </button>
                        <button
                          className="btn btn-icon-text btn-danger-soft"
                          onClick={(e) => { e.stopPropagation(); showConfirmModal('delete', user); }}
                          title={t('admu.deleteUserTitle')}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {t('admu.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredUsers.length > usersPerPage && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span className="pagination-info">
            {t('admu.pageOf', { current: currentPage, total: Math.ceil(filteredUsers.length / usersPerPage) })}
          </span>
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredUsers.length / usersPerPage), p + 1))}
            disabled={currentPage === Math.ceil(filteredUsers.length / usersPerPage)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {/*
        * Shown once. The password is not stored anywhere and cannot be read
        * back after this closes — generating a new one is the only way.
        */}
      {tempPassword && (
        <div className="modal-overlay" onClick={() => setTempPassword(null)}>
          <div className="modal temp-password-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('admu.tempPasswordTitle')}</h3>
            <p className="temp-password-intro">
              {t('admu.tempPasswordSendTo')} <b>{tempPassword.email}</b>. {t('admu.tempPasswordShownOnce')}
            </p>

            <div className="temp-password-box">
              <div>
                <span className="temp-password-label">Username</span>
                <code>{tempPassword.username}</code>
              </div>
              <div>
                <span className="temp-password-label">{t('admu.passwordLabel')}</span>
                <code className="temp-password-value">{tempPassword.password}</code>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => {
                const msg = t('admu.clientMessage', { username: tempPassword.username, password: tempPassword.password });
                navigator.clipboard?.writeText(msg);
              }}
            >
              {t('admu.copyClientMessage')}
            </button>
            <button className="btn btn-secondary" onClick={() => setTempPassword(null)}>
              {t('admu.close')}
            </button>
          </div>
        </div>
      )}

      {confirmModal.show && (
        <div className="modal-overlay" onClick={hideConfirmModal}>
          <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal-icon">
              {confirmModal.type === 'delete' ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6H5H21" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : confirmModal.type === 'disable' ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/>
                  <path d="M4.93 4.93L19.07 19.07" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="#3fffd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="#3fffd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 className="confirm-modal-title">
              {confirmModal.type === 'delete' && t('admu.deleteUserHeading')}
              {confirmModal.type === 'disable' && t('admu.disableUserHeading')}
              {confirmModal.type === 'enable' && t('admu.enableUserHeading')}
            </h3>
            <p className="confirm-modal-text">
              {confirmModal.type === 'delete' && (
                <>{t('admu.confirmDeletePre')} <strong>{confirmModal.user?.username}</strong>? {t('admu.cannotUndo')}</>
              )}
              {confirmModal.type === 'disable' && (
                <>{t('admu.confirmDisablePre')} <strong>{confirmModal.user?.username}</strong>?</>
              )}
              {confirmModal.type === 'enable' && (
                <>{t('admu.confirmEnablePre')} <strong>{confirmModal.user?.username}</strong>?</>
              )}
            </p>
            <div className="confirm-modal-actions">
              <button className="btn btn-cancel" onClick={hideConfirmModal}>
                {t('admu.noCancel')}
              </button>
              <button 
                className={`btn ${confirmModal.type === 'delete' ? 'btn-danger' : confirmModal.type === 'disable' ? 'btn-warning' : 'btn-success'}`}
                onClick={handleConfirmAction}
              >
                {t('admu.yesContinue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buildings Side Panel */}
      {showSidePanel && (
        <>
          <div className="side-panel-overlay" onClick={closeSidePanel} />
          <div className="side-panel">
            <div className="side-panel-header">
              <div className="side-panel-title">
                <h2>{t('admu.buildings')}</h2>
                {sidePanelUser && (
                  <span className={`plan-badge-small plan-${sidePanelUser.subscription_plan || 'basic'}`}>
                    {PLAN_CONFIG[sidePanelUser.subscription_plan || 'basic']?.label}
                  </span>
                )}
              </div>
              {sidePanelUser && (
                <p className="side-panel-subtitle">
                  {sidePanelUser.full_name || sidePanelUser.username}
                </p>
              )}
              <button className="side-panel-close" onClick={closeSidePanel}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="side-panel-body">
              {sidePanelLoading ? (
                <div className="side-panel-loading">
                  <div className="spinner"></div>
                  <span>{t('admu.loading')}</span>
                </div>
              ) : (
                <>
                  {/* Active Buildings Section - Compact List View */}
                  <div className="buildings-section">
                    <div className="section-header">
                      <h3>{t('admu.activeBuildings')}</h3>
                      <span className="section-count">
                        {sidePanelBuildings.filter(b => !b.archived).length} / {sidePanelUser?.planLimit || sidePanelUser?.building_limit || 1}
                      </span>
                    </div>
                    
                    {sidePanelBuildings.filter(b => !b.archived).length === 0 ? (
                      <p className="empty-section">{t('admu.noActiveBuildings')}</p>
                    ) : (
                      <div className="building-list-compact">
                        {sidePanelBuildings.filter(b => !b.archived).map(building => (
                          <div key={building.id} className="building-list-item">
                            {editingBuildingId === building.id ? (
                              <div className="building-edit-row">
                                <input
                                  type="text"
                                  className="building-name-input"
                                  value={editingBuildingName}
                                  onChange={(e) => setEditingBuildingName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveBuildingName(building.id);
                                    if (e.key === 'Escape') cancelEditingBuilding();
                                  }}
                                  autoFocus
                                  placeholder={t('admu.buildingNamePlaceholder')}
                                />
                                <button 
                                  className="btn-icon save"
                                  onClick={() => saveBuildingName(building.id)}
                                  disabled={savingBuildingId === building.id}
                                  title={t('admu.save')}
                                >
                                  {savingBuildingId === building.id ? (
                                    <div className="btn-spinner-small"></div>
                                  ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </button>
                                <button 
                                  className="btn-icon cancel"
                                  onClick={cancelEditingBuilding}
                                  title={t('admu.cancel')}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="building-list-info">
                                  <div className="building-list-name" onClick={() => startEditingBuilding(building)}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="edit-icon">
                                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span className={!building.name || building.name === 'Unnamed' ? 'unnamed' : ''}>
                                      {getBuildingDisplayName(building)}
                                    </span>
                                  </div>
                                  <div className="building-list-meta">
                                    {building.city && <span>{building.city}</span>}
                                    {building.city && (building.invoice_count > 0 || building.emission_count > 0) && <span className="meta-sep">•</span>}
                                    {building.invoice_count > 0 && <span>{t('admu.invoiceCount', { count: building.invoice_count })}</span>}
                                    {building.invoice_count > 0 && building.emission_count > 0 && <span className="meta-sep">•</span>}
                                    {building.emission_count > 0 && <span>{t('admu.emissionCount', { count: building.emission_count })}</span>}
                                  </div>
                                </div>
                                <button 
                                  className="btn-archive"
                                  onClick={() => archiveBuilding(building.id)}
                                  disabled={archivingBuildingId === building.id}
                                  title={t('admu.archive')}
                                >
                                  {archivingBuildingId === building.id ? (
                                    <div className="btn-spinner-small"></div>
                                  ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                      <path d="M21 8V21H3V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M23 3H1V8H23V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M10 12H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Archived Buildings Section - Compact List View */}
                  <div className="buildings-section archived-section">
                    <div className="section-header">
                      <h3>{t('admu.archivedBuildings')}</h3>
                      <span className="section-count archived">
                        {sidePanelBuildings.filter(b => b.archived).length}
                      </span>
                    </div>
                    
                    {sidePanelBuildings.filter(b => b.archived).length === 0 ? (
                      <p className="empty-section">{t('admu.noArchivedBuildings')}</p>
                    ) : (
                      <div className="building-list-compact">
                        {sidePanelBuildings.filter(b => b.archived).map(building => {
                          const canRestore = (sidePanelBuildings.filter(b => !b.archived).length) < (sidePanelUser?.planLimit || 1);
                          
                          return (
                            <div key={building.id} className="building-list-item archived">
                              <div className="building-list-info">
                                <div className="building-list-name archived">
                                  <span>{getBuildingDisplayName(building)}</span>
                                </div>
                                <div className="building-list-meta">
                                  {building.archived_at && (
                                    <span className="archived-date">{t('admu.archivedOn', { date: formatDate(building.archived_at) })}</span>
                                  )}
                                  {building.archived_at && building.city && <span className="meta-dot">•</span>}
                                  {building.city && <span>{building.city}</span>}
                                </div>
                              </div>
                              <button 
                                className={`btn-restore ${!canRestore ? 'disabled' : ''}`}
                                onClick={() => canRestore && restoreBuilding(building.id)}
                                disabled={!canRestore || restoringBuildingId === building.id}
                                title={!canRestore ? t('admu.planLimitReached') : t('admu.restoreBuilding')}
                              >
                                {restoringBuildingId === building.id ? (
                                  <div className="btn-spinner-small"></div>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M16 12L12 8M12 8L8 12M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Downgrade Modal */}
      {showDowngradeModal && downgradeUser && (
        <div className="modal-overlay" onClick={cancelDowngrade}>
          <div className="modal-content downgrade-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admu.chooseBuildingsTitle')}</h2>
              <button className="modal-close" onClick={cancelDowngrade}>&times;</button>
            </div>
            
            <div className="modal-body">
              <p className="downgrade-info">
                {t('admu.user')} <strong>{downgradeUser.username}</strong> {t('admu.dgHas')}{' '}
                <strong>{userBuildings.length}</strong> {t('admu.dgActiveButPlan')}{' '}
                <strong>{PLAN_CONFIG[downgradeNewPlan]?.label}</strong> {t('admu.dgAllowsOnly')}{' '}
                <strong>{PLAN_CONFIG[downgradeNewPlan]?.limit}</strong>.
              </p>
              <p className="downgrade-warning">
                {t('admu.downgradeWarning', { count: PLAN_CONFIG[downgradeNewPlan]?.limit })}
              </p>

              {loadingBuildings ? (
                <div className="loading-buildings">{t('admu.loadingBuildings')}</div>
              ) : (
                <div className="buildings-list">
                  {userBuildings.map(building => {
                    const isSelected = selectedBuildingIds.includes(building.id);
                    const isAtLimit = selectedBuildingIds.length >= (PLAN_CONFIG[downgradeNewPlan]?.limit || 1);
                    const isDisabled = !isSelected && isAtLimit;
                    
                    return (
                      <div 
                        key={building.id}
                        className={`building-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && handleBuildingSelect(building.id)}
                      >
                        <div className="building-checkbox">
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && handleBuildingSelect(building.id)}
                          />
                        </div>
                        <div className="building-info">
                          <div className="building-name">{building.name}</div>
                          <div className="building-details">
                            {building.address && <span>{building.address}</span>}
                            {building.city && <span> • {building.city}</span>}
                            {building.building_type && <span> • {building.building_type}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="selection-counter">
                {t('admu.selected')} <strong>{selectedBuildingIds.length}</strong> / {PLAN_CONFIG[downgradeNewPlan]?.limit}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-cancel" onClick={cancelDowngrade}>
                {t('admu.cancel')}
              </button>
              <button 
                className="btn btn-confirm"
                onClick={confirmDowngrade}
                disabled={selectedBuildingIds.length !== PLAN_CONFIG[downgradeNewPlan]?.limit}
              >
                {t('admu.confirmChange')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
